import { useCallback, useEffect, useState } from "react";
import { Download, Heart, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { MasonryCard } from "@/components/MediaCard";
import Lightbox from "@/components/Lightbox";

const TYPES = [["", "Vse"], ["photo", "Fotografije"], ["video", "Videi"]];

export default function Gallery({ slug, wedding, token, refreshKey }) {
  const [type, setType] = useState("");
  const [uploader, setUploader] = useState("");
  const [sort, setSort] = useState("new");
  const [favorites, setFavorites] = useState(false);
  const [data, setData] = useState({ items: [], uploaders: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/public/w/${slug}/media`, { params: { type: type || undefined, uploader: uploader || undefined, sort, favorites: favorites || undefined }, headers: { "X-Guest-Token": token } });
    setData(r.data);
    setLoading(false);
  }, [slug, type, uploader, sort, favorites, token]);

  useEffect(() => { load(); const t = setInterval(() => document.visibilityState === "visible" && load(), 12000); return () => clearInterval(t); }, [load, refreshKey]);

  const heart = async (item) => {
    try {
      const r = await api.post(`/public/w/${slug}/media/${item.id}/heart`, {}, { headers: { "X-Guest-Token": token } });
      setData((d) => ({ ...d, items: d.items.map((m) => (m.id === item.id ? { ...m, ...r.data } : m)) }));
    } catch (e) { toast.error("Srčka ni bilo mogoče dodati."); }
  };

  if (data.hidden) return <p className="text-center text-[#6C6560] py-16" data-testid="gallery-hidden">Mladoporočenca sta galerijo za goste skrila. Fotografije lahko še vedno dodajaš.</p>;
  const reactions = wedding.settings.reactions_enabled;

  return (
    <section data-testid="guest-gallery">
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 -mx-4 px-4">
        {TYPES.map(([v, l]) => (
          <button key={v} onClick={() => { setType(v); setFavorites(false); }} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${type === v && !favorites ? "bg-[#231F20] text-white border-[#231F20]" : "border-[#DFD5C5] bg-[#FFFDF9]"}`} data-testid={`gallery-filter-${v || "all"}`}>{l}</button>
        ))}
        {data.has_favorites && (
          <button onClick={() => setFavorites(!favorites)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border ${favorites ? "bg-[#C5A880] text-white border-[#C5A880]" : "border-[#DFD5C5] bg-[#FFFDF9]"}`} data-testid="gallery-filter-favorites">Najini najljubši trenutki</button>
        )}
        <select value={uploader} onChange={(e) => setUploader(e.target.value)} className="px-3 py-2 rounded-full text-sm border border-[#DFD5C5] bg-[#FFFDF9] max-w-[11rem]" data-testid="gallery-filter-uploader">
          <option value="">Vsi gostje</option>
          {data.uploaders.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-full text-sm border border-[#DFD5C5] bg-[#FFFDF9]" data-testid="gallery-sort">
          <option value="new">Najnovejše</option>
          <option value="old">Najstarejše</option>
          {reactions && <option value="hearts">Največ srčkov</option>}
        </select>
      </div>
      {uploader && (
        <div className="mb-4 flex items-center gap-2 text-sm" data-testid="gallery-uploader-active">
          <span className="text-[#6C6560]">Spomini gosta</span><span className="font-display text-xl">{uploader}</span>
          <button onClick={() => setUploader("")} className="p-1 rounded-full hover:bg-[#EADBCE]" data-testid="gallery-uploader-clear" aria-label="Počisti"><X size={14} /></button>
        </div>
      )}
      {loading ? (
        <div className="columns-2 sm:columns-3 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="mb-3 rounded-xl bg-[#F3ECE1] animate-pulse" style={{ height: 140 + (i % 3) * 60 }} />)}</div>
      ) : data.items.length === 0 ? (
        <p className="text-center text-[#6C6560] py-16" data-testid="gallery-empty">Tukaj še ni spominov. Bodi prvi, ki doda fotografijo!</p>
      ) : (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-3">
          {data.items.map((m, i) => (
            <MasonryCard key={m.id} item={m} reactions={reactions} onOpen={() => setOpen(i)} onHeart={heart} onUploader={(n) => { setUploader(n); window.scrollTo({ top: document.querySelector('[data-testid="guest-gallery"]').offsetTop - 80, behavior: "smooth" }); }} />
          ))}
        </div>
      )}
      <Lightbox items={data.items} index={open} onClose={() => setOpen(null)} onIndex={setOpen} actions={(item) => (
        <>
          {reactions && (
            <button onClick={() => heart(item)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-heart">
              <Heart size={18} className={item.liked ? "fill-[#E8B4A8] text-[#E8B4A8]" : ""} /> {item.hearts || 0}
            </button>
          )}
          {item.download_url && (
            <a href={fileUrl(item.download_url)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-download"><Download size={18} /> Prenesi</a>
          )}
        </>
      )} />
    </section>
  );
}
